// @ts-nocheck
import { useState } from "react";
import { canManagePortalUsers } from "@/services/jambushrustiAuth";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  BookPlus,
  BookCheck,
  ScrollText,
  Users,
  CheckSquare,
  BarChart3,
  Ticket,
  Settings,
  Warehouse,
  ChevronLeft,
  Search,
  Menu,
  X,
  Library,
  IndianRupee,
} from "lucide-react";
import { cn } from "@libraryadmin/lib/utils";
import { Input } from "@libraryadmin/components/ui/input";
import { Button } from "@libraryadmin/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { getBookCount } from "@libraryadmin/services/api";
import { getLibraryAdminSession } from "@libraryadmin/services/auth";
import {
  LotusRule,
  TempleGateLogo,
} from "@jambushrushti/site/components/Ornaments";

const menuItems = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/vault-library-admin/dashboard",
    category: "main",
  },
  {
    icon: BookPlus,
    label: "Book Entry",
    path: "/vault-library-admin/book-entry",
    category: "catalog",
  },
  {
    icon: BookOpen,
    label: "Book Issue",
    path: "/vault-library-admin/book-issue",
    category: "catalog",
  },
  {
    icon: BookCheck,
    label: "Book Receive",
    path: "/vault-library-admin/book-receive",
    category: "catalog",
  },
  {
    icon: Warehouse,
    label: "Storage",
    path: "/vault-library-admin/storage",
    category: "catalog",
  },
  {
    icon: ScrollText,
    label: "Manuscripts",
    path: "/vault-library-admin/manuscripts",
    category: "jain",
  },
  {
    icon: Users,
    label: "Members",
    path: "/vault-library-admin/members",
    category: "management",
  },
  {
    icon: Users,
    label: "Roles & Permissions",
    path: "/vault-library-admin/users",
    category: "management",
  },

  {
    icon: IndianRupee,
    label: "Payment",
    path: "/vault-library-admin/fine-management",
    category: "management",
  },
  // {
  //   icon: CheckSquare,
  //   label: "Approvals",
  //   path: "/library-admin/approvals",
  //   category: "management",
  // },
  {
    icon: BarChart3,
    label: "Reports",
    path: "/vault-library-admin/reports",
    category: "analytics",
  },
  // {
  //   icon: Ticket,
  //   label: "Tickets",
  //   path: "/library-admin/tickets",
  //   category: "support",
  // },
  // {
  //   icon: Settings,
  //   label: "Settings",
  //   path: "/library-admin/settings",
  //   category: "system",
  // },
];

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

export function Sidebar({
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
}: SidebarProps) {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const { data: totalBooks } = useQuery({
    queryKey: ["dashboard-stats", "book-count"],
    queryFn: getBookCount,
    staleTime: 60_000,
  });

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      setMobileOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 z-50 flex h-screen flex-col border-r border-sidebar-border shadow-xl transition-all duration-300",
          collapsed ? "w-[72px]" : "w-72",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        style={{
          background: "var(--gradient-sidebar)",
        }}
      >
        {/* gold hairline along the inner edge, like a book's spine (as on the sign-in panel) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-[3px] opacity-60"
          style={{
            background:
              "linear-gradient(180deg, transparent, #c8964a 15%, #c8964a 85%, transparent)",
          }}
        />

        {/* Logo Section */}
        <div className="relative flex items-center gap-3 border-b border-sidebar-border px-4 py-5">
          <TempleGateLogo className="h-11 w-10 flex-shrink-0 text-[#d9a95f]" />
          {!collapsed && (
            <div className="min-w-0">
              <h1 className="font-display text-[19px] font-semibold leading-tight tracking-wide text-[#fbecd0]">
                Jambushrushti
              </h1>
              <LotusRule className="my-1 block h-2.5 w-28 text-[#c8964a]" />
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#e2b86a]">
                Library Administration
              </p>
            </div>
          )}
          {/* Mobile Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto text-sidebar-foreground hover:bg-sidebar-accent lg:hidden"
            onClick={() => setMobileOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Search */}
        {!collapsed && (
          <div className="px-4 py-4">
            <div className="group relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-foreground/50 transition-colors group-focus-within:text-[#e2b86a]" />
              <Input
                placeholder="Search library..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="h-11 rounded-lg border-sidebar-border bg-black/15 pl-9 text-sidebar-foreground transition-all duration-300 placeholder:text-sidebar-foreground/45 focus:border-[#c8964a]/60 focus:bg-black/25 focus-visible:ring-1 focus-visible:ring-[#c8964a]/40 focus-visible:ring-offset-0"
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="relative flex-1 overflow-y-auto px-3 py-2">
          {/* Quick Access Label */}
          {!collapsed && (
            <div className="mb-2 px-3 py-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#e2b86a]/80">
                Navigation
              </span>
            </div>
          )}

          <ul className="space-y-1">
            {(getLibraryAdminSession()?.can_access_library
              ? menuItems.filter(
                  item =>
                    (item.path !== "/vault-library-admin/users" ||
                      canManagePortalUsers(getLibraryAdminSession())) &&
                    item.label
                      .toLowerCase()
                      .includes(searchQuery.trim().toLowerCase())
                )
              : []
            ).map((item, index) => {
              const isActive =
                item.path === "/vault-library-admin/dashboard"
                  ? location.pathname === item.path
                  : location.pathname === item.path ||
                    location.pathname.startsWith(`${item.path}/`);
              return (
                <li
                  key={item.path}
                  className="animate-slide-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                >
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={cn(
                      "sidebar-item group",
                      isActive && "sidebar-item-active",
                      collapsed && "justify-center px-2"
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon
                      className={cn(
                        "h-[18px] w-[18px] flex-shrink-0 transition-all duration-300 group-hover:scale-110",
                        isActive
                          ? "text-[#8c3c15]"
                          : "text-sidebar-foreground/70 group-hover:text-sidebar-foreground"
                      )}
                    />
                    {!collapsed && (
                      <span
                        className={cn(
                          "text-[13px] font-medium transition-colors",
                          isActive
                            ? "font-semibold text-[#5e2208]"
                            : "text-sidebar-foreground/80 group-hover:text-sidebar-foreground"
                        )}
                      >
                        {item.label}
                      </span>
                    )}
                    {!collapsed && item.category === "jain" && (
                      <span className="ml-auto rounded-full border border-[#c8964a]/40 bg-[#c8964a]/15 px-2 py-0.5 text-[9px] font-bold tracking-wide text-[#c8964a]">
                        Jain
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="relative border-t border-sidebar-border px-3 py-4">
          {!collapsed && (
            <div className="mb-3 flex items-center gap-3 rounded-lg border border-[#c8964a]/30 bg-black/15 px-3 py-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#c8964a]/20">
                <Library className="h-4 w-4 text-[#e2b86a]" />
              </div>
              <div className="text-[12px] text-sidebar-foreground/80">
                <span className="font-display text-base font-semibold text-[#fbecd0]">
                  {totalBooks !== undefined ? totalBooks.toLocaleString() : "—"}
                </span>
                <span className="block text-[10px] text-sidebar-foreground/60">
                  Books in Library
                </span>
              </div>
            </div>
          )}

          {/* Collapse Button - Desktop Only */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="sidebar-item hidden w-full hover:bg-sidebar-accent lg:flex"
          >
            <ChevronLeft
              className={cn(
                "h-4 w-4 text-sidebar-foreground/70 transition-transform duration-300",
                collapsed && "rotate-180"
              )}
            />
            {!collapsed && (
              <span className="text-[13px] font-medium text-sidebar-foreground/80">
                Collapse
              </span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}

export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="hover:bg-muted lg:hidden"
      onClick={onClick}
    >
      <Menu className="h-5 w-5" />
    </Button>
  );
}
